/**
 * Dashboard widget: today's carrier cutoffs with live countdown badges.
 * Times are static per day - the countdown ticks locally, no API polling.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Truck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Badge } from '../ui';
import * as carrierCutoffsApi from '../../services/carrierCutoffs.api';
import {
  getCutoffStatus,
  getCutoffBadgeVariant,
  hhmmToOperationalMinutes,
} from '../../utils/carrierCutoffStatus';

export default function CarrierCutoffsCard() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);

  const { data: carriers, isLoading } = useQuery({
    queryKey: ['carrierCutoffs'],
    queryFn: () => carrierCutoffsApi.getCarrierCutoffs(),
  });

  const configured = (carriers || [])
    .filter((c) => c.cutoff)
    .sort(
      (a, b) =>
        hhmmToOperationalMinutes(a.cutoff!.cutoff_time) -
        hhmmToOperationalMinutes(b.cutoff!.cutoff_time)
    );

  return (
    <Card padding="none">
      <CardHeader className="px-4 pt-4 pb-2 border-b border-gray-100">
        <CardTitle className="text-sm flex items-center gap-2">
          <Truck className="w-4 h-4 text-blue-600" />
          Carrier Cutoffs Today
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
        {!isLoading && configured.length === 0 && (
          <p className="text-sm text-gray-500">No carrier cutoffs configured yet.</p>
        )}
        <div className="space-y-2">
          {configured.map((carrier) => {
            const status = getCutoffStatus(carrier.cutoff!.cutoff_time, now);
            return (
              <div key={carrier.carrier_id} className="flex items-center justify-between">
                <span className="text-sm text-gray-700">{carrier.carrier_title}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">
                    {carrier.cutoff!.cutoff_time}
                  </span>
                  <Badge size="sm" variant={getCutoffBadgeVariant(status.state)}>
                    {status.label}
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>
        <Link
          to="/carrier-cutoffs"
          className="block mt-3 text-xs font-medium text-blue-600 hover:text-blue-700"
        >
          Manage carrier cutoffs →
        </Link>
      </CardContent>
    </Card>
  );
}
